import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './MentionTextarea.module.scss';

function normalizeMentions(value) {
    if (!Array.isArray(value)) {
        return [];
    }

    const unique = new Map();
    value.forEach((item) => {
        const id = Number(item?.id || 0);
        const label = String(item?.label || '').trim();
        if (id > 0 && label) {
            unique.set(id, { id, label });
        }
    });

    return Array.from(unique.values());
}

function pruneMentionsByText(text, mentions) {
    const normalizedText = String(text || '');
    return normalizeMentions(mentions).filter((mention) => normalizedText.includes(`@${mention.label}`));
}

function findActiveMention(value, caretPosition) {
    const safeValue = String(value || '');
    const cursor = Number.isFinite(caretPosition) ? caretPosition : safeValue.length;
    const beforeCaret = safeValue.slice(0, cursor);
    const match = beforeCaret.match(/(^|\s)@([^\s@]*)$/u);

    if (!match) {
        return null;
    }

    const query = match[2] || '';
    return {
        query,
        start: cursor - query.length - 1,
        end: cursor,
    };
}

export default function MentionTextarea({
    value,
    onChange,
    mentions,
    onMentionsChange,
    candidates,
    placeholder,
    disabled = false,
    rows = 4,
    className = '',
    onKeyDown,
    onPaste,
}) {
    const textareaRef = useRef(null);
    const blurTimeoutRef = useRef(null);
    const pendingCaretRef = useRef(null);

    const [activeMention, setActiveMention] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const normalizedMentions = useMemo(() => normalizeMentions(mentions), [mentions]);
    const normalizedCandidates = useMemo(() => normalizeMentions(candidates), [candidates]);

    const suggestions = useMemo(() => {
        if (!activeMention) {
            return [];
        }

        const query = activeMention.query.toLowerCase();
        const usedIds = new Set(normalizedMentions.map((item) => item.id));

        return normalizedCandidates.filter((candidate) => {
            if (usedIds.has(candidate.id)) {
                return false;
            }

            return candidate.label.toLowerCase().includes(query);
        }).slice(0, 8);
    }, [activeMention, normalizedCandidates, normalizedMentions]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [activeMention?.query]);

    useEffect(() => {
        if (pendingCaretRef.current === null || !textareaRef.current) {
            return;
        }

        const caret = pendingCaretRef.current;
        pendingCaretRef.current = null;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(caret, caret);
    }, [value]);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const syncMentionState = (nextValue, caretPosition) => {
        const nextMentions = pruneMentionsByText(nextValue, normalizedMentions);
        if (JSON.stringify(nextMentions) !== JSON.stringify(normalizedMentions)) {
            onMentionsChange(nextMentions);
        }
        setActiveMention(findActiveMention(nextValue, caretPosition));
    };

    const handleChange = (event) => {
        const nextValue = event.target.value;
        const nextCaret = event.target.selectionStart ?? nextValue.length;
        onChange(nextValue);
        syncMentionState(nextValue, nextCaret);
    };

    const insertMention = (candidate) => {
        if (!activeMention) {
            return;
        }

        const currentValue = String(value || '');
        const mentionText = `@${candidate.label} `;
        const nextValue = `${currentValue.slice(0, activeMention.start)}${mentionText}${currentValue.slice(activeMention.end)}`;
        const nextMentions = normalizeMentions([...normalizedMentions, candidate]);
        const nextCaret = activeMention.start + mentionText.length;

        pendingCaretRef.current = nextCaret;
        onChange(nextValue);
        onMentionsChange(nextMentions);
        setActiveMention(null);
    };

    const handleKeyDown = (event) => {
        if (activeMention && suggestions.length > 0) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedIndex((prev) => (prev + 1) % suggestions.length);
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
                return;
            }

            if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                insertMention(suggestions[selectedIndex] || suggestions[0]);
                return;
            }

            if (event.key === 'Tab') {
                event.preventDefault();
                insertMention(suggestions[selectedIndex] || suggestions[0]);
                return;
            }

            if (event.key === 'Escape') {
                setActiveMention(null);
                return;
            }
        }

        onKeyDown?.(event);
    };

    const handleSelectCandidate = (candidate) => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }
        insertMention(candidate);
    };

    const handleBlur = () => {
        blurTimeoutRef.current = setTimeout(() => {
            setActiveMention(null);
        }, 120);
    };

    const handleFocusOrClick = (event) => {
        const caretPosition = event.target.selectionStart ?? String(value || '').length;
        setActiveMention(findActiveMention(value, caretPosition));
    };

    return (
        <div className={styles.wrapper}>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onClick={handleFocusOrClick}
                onFocus={handleFocusOrClick}
                onKeyDown={handleKeyDown}
                onPaste={onPaste}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                rows={rows}
                className={`${styles.textarea} ${className}`.trim()}
            />

            {activeMention && suggestions.length > 0 ? (
                <div className={styles.dropdown}>
                    {suggestions.map((candidate, index) => (
                        <button
                            key={candidate.id}
                            type="button"
                            className={index === selectedIndex ? styles.itemActive : styles.item}
                            onMouseDown={(event) => {
                                event.preventDefault();
                                handleSelectCandidate(candidate);
                            }}
                        >
                            @{candidate.label}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
}
